import React, { useState, useEffect, useRef } from "react";
import CircularLoading from "../components/CircularLoading";
import BookingDataLoadError from "../components/BookingDataLoadError";
import { useSelector, useDispatch } from "react-redux";
import ConfirmationDialogRaw from "../components/ConfirmationDialogRaw";
import { FONT_FAMILY, downloadCsv, formatCurrency, labelText } from "../common/sharedFunctions";
import {
  Box,
  Grid,
  Typography,
  Modal,
  Button,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Menu,
  IconButton,
  Tooltip,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { api } from "common";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UsersCombo from "../components/UsersCombo";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import EmailIcon from "@mui/icons-material/Email";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ReplayIcon from "@mui/icons-material/Replay";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AlertDialog from "../components/AlertDialog";
import { colors } from "../components/Theme/WebTheme";
import { useNavigate,useLocation } from "react-router-dom";
import {
  bookingHistoryColumns,
  BidModal,
  acceptBid,
  getBookingStatusLabel,
  getActiveDriverOffers,
  getActiveFleetOffers,
  getFinalPaymentAmount,
  showEst,
} from "../common/sharedFunctions";
import localization from '../components/Table/Localization';
import { canDoAction } from "common/src/actions/userPermissions";
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { ActionButton, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: 680,
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${colors.BLACK}`,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  action: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "center",
    borderRadius: "20px",
    paddingLeft: "10px",
    paddingRight: "10px",
    width: "10rem",
    minHeight:"40px"
  },
}));

const icons = {
  paypal: require("../assets/payment-icons/paypal-logo.png").default,
  braintree: require("../assets/payment-icons/braintree-logo.png").default,
  stripe: require("../assets/payment-icons/stripe-logo.png").default,
  paytm: require("../assets/payment-icons/paytm-logo.png").default,
  payulatam: require("../assets/payment-icons/payulatam-logo.png").default,
  flutterwave: require("../assets/payment-icons/flutterwave-logo.png").default,
  paystack: require("../assets/payment-icons/paystack-logo.png").default,
  securepay: require("../assets/payment-icons/securepay-logo.png").default,
  payfast: require("../assets/payment-icons/payfast-logo.png").default,
  liqpay: require("../assets/payment-icons/liqpay-logo.png").default,
  culqi: require("../assets/payment-icons/culqi-logo.png").default,
  mercadopago: require("../assets/payment-icons/mercadopago-logo.png").default,
  squareup: require("../assets/payment-icons/squareup-logo.png").default,
  wipay: require("../assets/payment-icons/wipay-logo.png").default,
  test: require("../assets/payment-icons/test-logo.png").default,
  razorpay: require("../assets/payment-icons/razorpay-logo.png").default,
  paymongo:require("../assets/payment-icons/paymongo-logo.png").default,
  gcash: require("../assets/payment-icons/paymongo-logo.png").default,
  paymaya: require("../assets/payment-icons/paymongo-logo.png").default,
  iyzico:require("../assets/payment-icons/iyzico-logo.png").default,
};

const paymentProviderLabel = (provider) => {
  if (!provider) return "";
  if (provider.name === "gcash") return "GCash";
  if (provider.name === "paymaya") return "PayMaya";
  return "";
};

const staleReceiptLabels = {
  send_booking_receipt: "Send Booking Receipt",
  booking_receipt_sent: "Booking receipt sent",
  booking_receipt_failed: "Unable to send booking receipt",
};

const oldReceiptLabels = {
  send_booking_receipt: "Send Booking Details",
  booking_receipt_sent: "Booking details sent",
  booking_receipt_failed: "Unable to send booking details",
};

const reorderEligibleStatuses = ["COMPLETE", "CANCELLED"];
const fleetAdminCancelableStatuses = ["NEW", "AWAITING_DRIVER_ASSIGNMENT", "PAYMENT_PENDING"];

const BookingRowActions = ({ actions, moreLabel }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const detailsAction = actions.find((action) => action.id === "details");
  const primaryAction = actions.find((action) => action.primary && !action.disabled);
  const menuActions = actions.filter(
    (action) => action.id !== detailsAction?.id && action.id !== primaryAction?.id
  );
  const menuOpen = Boolean(menuAnchor);

  const stopAndRun = (event, action) => {
    event.stopPropagation();
    if (!action.disabled) {
      action.onClick();
    }
  };

  const closeMenu = (event) => {
    if (event) event.stopPropagation();
    setMenuAnchor(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 0.5,
        width: "100%",
        minWidth: 0,
        whiteSpace: "nowrap",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {detailsAction ? (
        <Tooltip title={detailsAction.label} arrow>
          <IconButton
            size="small"
            aria-label={detailsAction.label}
            onClick={(event) => stopAndRun(event, detailsAction)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              color: adminTokens.colors.deepNavy,
              border: `1px solid ${adminTokens.colors.border}`,
              bgcolor: "#fff",
              "&:hover": { bgcolor: "rgba(11,124,255,.08)", borderColor: "rgba(11,124,255,.35)" },
              "&:focus-visible": { boxShadow: adminTokens.shadows.focus },
            }}
          >
            {detailsAction.icon}
          </IconButton>
        </Tooltip>
      ) : null}

      {primaryAction ? (
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={primaryAction.icon}
          onClick={(event) => stopAndRun(event, primaryAction)}
          sx={{
            minWidth: 0,
            height: 32,
            px: 1.15,
            borderRadius: 2,
            textTransform: "none",
            whiteSpace: "nowrap",
            fontSize: "0.72rem",
            fontWeight: 850,
            bgcolor: adminTokens.colors.electricBlue,
            "&:hover": { bgcolor: adminTokens.colors.deepNavy },
            "&:focus-visible": { boxShadow: adminTokens.shadows.focus },
            "& .MuiButton-startIcon": { mr: 0.55, "& svg": { fontSize: 17 } },
          }}
        >
          {primaryAction.label}
        </Button>
      ) : null}

      {menuActions.length ? (
        <>
          <Tooltip title={moreLabel} arrow>
            <IconButton
              size="small"
              aria-label={moreLabel}
              aria-haspopup="menu"
              aria-expanded={menuOpen ? "true" : undefined}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                color: adminTokens.colors.deepNavy,
                bgcolor: "rgba(3,29,54,.06)",
                "&:hover": { bgcolor: "rgba(255,107,0,.13)", color: adminTokens.colors.garahexOrange },
                "&:focus-visible": { boxShadow: adminTokens.shadows.focus },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              elevation: 8,
              sx: {
                mt: 0.5,
                minWidth: 224,
                maxWidth: 300,
                borderRadius: 2.5,
                border: `1px solid ${adminTokens.colors.border}`,
                overflow: "hidden",
              },
            }}
            MenuListProps={{ dense: true, "aria-label": moreLabel }}
          >
            {menuActions.map((action) => (
              <MenuItem
                key={action.id}
                disabled={action.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  closeMenu();
                  action.onClick();
                }}
                sx={{
                  minHeight: 38,
                  gap: 0.5,
                  fontSize: "0.78rem",
                  fontWeight: 750,
                  whiteSpace: "normal",
                  "&:focus-visible": { bgcolor: "rgba(11,124,255,.09)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: "30px !important", color: action.danger ? adminTokens.colors.danger : adminTokens.colors.deepNavy }}>
                  {action.icon}
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  primaryTypographyProps={{ fontSize: "0.78rem", fontWeight: 750 }}
                />
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}
    </Box>
  );
};

const canCancelBookingForRole = (booking, role) => {
  if (!booking) return false;
  if (role === "fleetadmin") return fleetAdminCancelableStatuses.indexOf(booking.status) !== -1;
  return booking.status === "NEW" || booking.status === "ACCEPTED";
};

const canPlaceOrderAgainForRole = (booking, role) => (
  !!booking && (role === "fleetadmin" || reorderEligibleStatuses.indexOf(booking.status) !== -1)
);

const getDraftAddress = (booking, addressKey, flatAddressKey) => {
  const address = booking && booking[addressKey] ? booking[addressKey] : {};
  const lat = address.lat !== undefined ? address.lat : null;
  const lng = address.lng !== undefined ? address.lng : null;
  const waypoints = address.waypoints || (addressKey === "drop" && booking && booking.waypoints ? booking.waypoints : null);
  const waypointsStr = address.waypointsStr || (Array.isArray(waypoints) ? waypoints.map((point) => `${point.lat},${point.lng}`).join("|") : null);
  return {
    add: address.add || booking?.[flatAddressKey] || "",
    description: address.add || booking?.[flatAddressKey] || "",
    coords: lat !== null && lng !== null ? { lat: parseFloat(lat), lng: parseFloat(lng) } : null,
    waypoints,
    waypointsStr,
    source: "reorder"
  };
};

const pickObjectFields = (source, keys) => {
  const picked = {};
  keys.forEach((key) => {
    if (source && source[key] !== undefined && source[key] !== null) {
      picked[key] = source[key];
    }
  });
  return picked;
};

const createReorderDraft = (booking) => {
  const instructionData = pickObjectFields(booking, [
    "serviceType",
    "shipmentType",
    "itemDescription",
    "grossWeight",
    "numberOfPieces",
    "cargoLength",
    "cargoWidth",
    "cargoHeight",
    "note",
    "notes",
    "tripInstructions",
    "pickUpInstructions",
    "deliveryInstructions",
    "exactDeliveryAddress",
    "preferredPickupDate",
    "preferredPickupTime",
    "receiverName",
    "receiverContactNumber",
    "pickupAddress",
    "deliveryAddress"
  ]);
  if (!instructionData.note && booking && (booking.notes || booking.instructions || booking.tripInstructions)) {
    instructionData.note = booking.notes || booking.instructions || booking.tripInstructions;
  }

  ["domesticBookingInfo", "importBookingInfo", "exportBookingInfo"].forEach((key) => {
    if (booking && booking[key]) {
      instructionData[key] = { ...booking[key] };
    }
  });

  return {
    pickupAddress: getDraftAddress(booking, "pickup", "pickupAddress"),
    dropAddress: getDraftAddress(booking, "drop", "dropAddress"),
    carType: booking?.carType || "",
    tripInstructions: booking?.tripInstructions || booking?.instructions || booking?.notes || "",
    customer: {
      uid: booking?.customer || "",
      firstName: booking?.customer_name ? booking.customer_name.split(" ")[0] || "" : "",
      lastName: booking?.customer_name ? booking.customer_name.split(" ").slice(1).join(" ") : "",
      mobile: booking?.customer_contact || "",
      email: booking?.customer_email || "",
      pushToken: booking?.customer_token || ""
    },
    instructionData
  };
};

const BookingHistory = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { cancelBooking, updateBooking, settleBookingPayment, RequestPushMsg, forceEndBooking, sendBookingReceipt, acceptFleetOffer, assignFleetBidDriver, assignFleetPrivateClientDriver, fetchCars } = api;
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const userdata = useSelector((state) => state.usersdata);
  const carlistdata = useSelector((state) => state.carlistdata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const role = useSelector((state) => state.auth.profile.usertype);
  const canEditBooking = canDoAction({ role, moduleKey: "bookingHistory", action: "edit", accessPermissions: settings && settings.accessPermissions, fallback: true });
  const canCreateBooking = canDoAction({ role, moduleKey: "addBookings", action: "create", accessPermissions: settings && settings.accessPermissions, fallback: true });
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const providers = useSelector((state) => state.paymentmethods.providers);
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [data, setData] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const [users, setUsers] = useState(null);
  const [userCombo, setUserCombo] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rowIndex, setRowIndex] = useState();
  const [bidModalStatus, setBidModalStatus] = useState(true);
  const [selectedBidder, setSelectedBidder] = useState();
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const classes = useStyles();
  const baseColumns = bookingHistoryColumns(role, settings, t, isRTL, auth.profile).map((column) => {
    if (column.field === "status") {
      return {
        ...column,
        render: (rowData) => (
          <AdminStatusBadge status={rowData.status}>
            {getBookingStatusLabel(rowData, role, t, auth.profile)}
          </AdminStatusBadge>
        )
      };
    }
    if (column.field === "paymentStatus") {
      return {
        ...column,
        render: (rowData) => <AdminStatusBadge status={getBookingStatusLabel(rowData, role, t, auth.profile)} />
      };
    }
    return column;
  });
  const [walletModalStatus, setWalletModalStatus] = useState(false);
  const {state} = useLocation();
  const [currentPage, setCurrentPage] = useState(0);
  const label = (key, fallback) => {
    const translated = t(key);
    if (oldReceiptLabels[key] && translated === oldReceiptLabels[key]) {
      return fallback || staleReceiptLabels[key];
    }
    const value = labelText(t, key, fallback || staleReceiptLabels[key]);
    return value === key ? (fallback || staleReceiptLabels[key]) : value;
  };

  const isPrivateClientBooking = (booking) => !!(
    booking && (booking.bookingSource === "fleet_admin_client" || booking.visibility === "private")
  );

  const canViewPrivateFinancials = (booking) => !!(
    isPrivateClientBooking(booking) && (
      role === "admin" ||
      (role === "fleetadmin" && (booking.fleetadmin === auth.profile.uid || booking.createdBy === auth.profile.uid))
    )
  );

  const formatFinancialCurrency = (value) => (
    value !== undefined && value !== null && value !== "" ? formatCurrency(value, settings) : ""
  );

  const formatRateValue = (type, value) => {
    if (value === undefined || value === null || value === "") return "";
    return type === "percentage" ? `${value}%` : formatFinancialCurrency(value);
  };

  const renderPrivateFinancialSummary = (booking) => {
    if (!canViewPrivateFinancials(booking)) return "";
    const summaryRows = [
      [labelText(t, "trucking_charge_to_client", "Trucking Charge to Client"), formatFinancialCurrency(booking.truckingChargeToClient)],
      [labelText(t, "super_admin_commission_type", "Super Admin Commission Type"), booking.superAdminCommissionType || ""],
      [labelText(t, "super_admin_commission_value", "Super Admin Commission Value"), formatRateValue(booking.superAdminCommissionType, booking.superAdminCommissionValue)],
      [labelText(t, "super_admin_commission_amount", "Super Admin Commission Amount"), formatFinancialCurrency(booking.superAdminCommissionAmount)],
      [labelText(t, "additional_fees_total", "Additional Fees Total"), formatFinancialCurrency(booking.totalAdditionalFeeAmount)],
      [labelText(t, "fleet_admin_net_amount", "Fleet Admin Net Amount"), formatFinancialCurrency(booking.fleetAdminNetAmount)],
      [labelText(t, "wallet_threshold_required_amount", "Wallet Threshold Required Amount"), formatFinancialCurrency(booking.walletThresholdRequiredAmount)],
      [labelText(t, "wallet_balance_at_booking", "Wallet Balance at Booking"), formatFinancialCurrency(booking.walletBalanceAtBooking)],
      [labelText(t, "wallet_threshold_passed", "Wallet Threshold Passed"), booking.walletThresholdPassed === true ? labelText(t, "true", "True") : booking.walletThresholdPassed === false ? labelText(t, "false", "False") : ""],
      [labelText(t, "assigned_driver", "Assigned Driver"), booking.assignedDriverName || booking.driver_name || booking.assignedDriver || booking.driver || ""],
      [labelText(t, "assigned_vehicle", "Assigned Vehicle"), booking.assignedVehicleName || booking.assignedVehiclePlate || booking.vehicle_number || booking.assignedVehicle || ""]
    ].filter((row) => row[1] !== "");
    return (
      <div style={{ minWidth: 320, paddingTop: 4, paddingBottom: 4 }}>
        {summaryRows.map(([rowLabel, value]) => (
          <div key={rowLabel} style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
            <span style={{ fontWeight: 600 }}>{rowLabel}</span>
            <span style={{ textAlign: "right" }}>{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const columns = role === "admin" || role === "fleetadmin"
    ? [
        ...baseColumns,
        {
          title: labelText(t, "private_booking_financial_summary", "Private Booking Financial Summary"),
          field: "privateBookingFinancialSummary",
          sorting: false,
          render: renderPrivateFinancialSummary
        }
      ]
    : baseColumns;
  
  useEffect(()=>{
    setCurrentPage(state?.pageNo)
  },[state])

  const handlePaginationChange = (page) => {
    setCurrentPage(page)
  };

  useEffect(() => {
    if (bookinglistdata.bookings) {
      setData(bookinglistdata.bookings);
    } else {
      setData([]);
    }
  }, [bookinglistdata.bookings]);

  useEffect(() => {
    if (role === "fleetadmin" && fetchCars) {
      dispatch(fetchCars());
    }
  }, [dispatch, fetchCars, role]);

  useEffect(() => {
    if (userdata.users) {
      let arr = [];
      for (let i = 0; i < userdata.users.length; i++) {
        let user = userdata.users[i];
        if (
          user.usertype === "driver" &&
          ((((user.fleetadmin || user.fleetadminUid || user.createdBy) === auth.profile.uid) && role === "fleetadmin") ||
            role === "admin")
        ) {
          arr.push({
            firstName: user.firstName,
            lastName: user.lastName,
            mobile: user.mobile,
            email: user.email,
            uid: user.id,
            desc:
              user.firstName +
              " " +
              user.lastName +
              " (" +
              (settings.AllowCriticalEditsAdmin
                ? user.mobile
                : t("hidden_demo")) +
              ") " +
              (settings.AllowCriticalEditsAdmin
                ? user.email
                : t("hidden_demo")),
            pushToken: user.pushToken ? user.pushToken : "",
            carType: user.carType,
          });
        }
      }
      setUsers(arr);
    }
  }, [
    userdata.users,
    settings.AllowCriticalEditsAdmin,
    auth.profile.uid,
    role,
    t,
  ]);

  const normalizeVehicleType = (value) => {
    const name = typeof value === "string" ? value : (value && value.name ? value.name : "");
    return name.toLowerCase().replace(/[^a-z0-9]/g, "");
  };

  const isFleetPrivateOwnerBooking = (booking) => !!(
    booking &&
    role === "fleetadmin" &&
    booking.bookingSource === "fleet_admin_client" &&
    booking.visibility === "private" &&
    (booking.fleetadmin === auth.profile.uid || booking.createdBy === auth.profile.uid)
  );

  const getPrivateAssignmentVehicles = (booking, driver) => {
    const cars = carlistdata && Array.isArray(carlistdata.cars) ? carlistdata.cars : [];
    const bookingVehicleType = normalizeVehicleType(booking && (booking.carType || booking.vehicleType));
    return cars.filter((car) => {
      if (!car || !driver || !bookingVehicleType) return false;
      const carFleetAdmin = car.fleetadmin || car.fleetAdmin || car.fleetadminId || car.fleetAdminId;
      const carDriver = car.driver || car.driverId || car.driverUid;
      const carVehicleType = normalizeVehicleType(car.carType || car.vehicleType || car.vehicle_type || car.name);
      const belongsToFleet = !carFleetAdmin || `${carFleetAdmin}` === `${auth.profile.uid}`;
      const matchesDriver = !carDriver || `${carDriver}` === `${driver.uid}`;
      const isActive = car.active !== false && car.approved !== false && car.carApproved !== false;
      return belongsToFleet && matchesDriver && isActive && carVehicleType === bookingVehicleType;
    });
  };

  const getVehicleLabel = (car) => {
    const rawName = car.name || car.carType || car.vehicleType || car.vehicleModel || "Vehicle";
    const name = typeof rawName === "string" ? rawName : (rawName && rawName.name ? rawName.name : "Vehicle");
    const plate = car.vehicleNumber || car.vehicle_number || car.plateNumber || "";
    return plate ? `${name} (${plate})` : name;
  };

  const assignDriver = () => {
    if (!canEditBooking) {
      setCommonAlert({ open: true, msg: label("action_permission_denied", "You do not have permission to edit bookings.") });
      return;
    }
    let booking = selectedBooking || data[rowIndex];
    if (!booking || !userCombo) {
      setCommonAlert({ open: true, msg: labelText(t, "select_driver", "Select Driver") });
      return;
    }
    if (isFleetPrivateOwnerBooking(booking)) {
      if (!selectedVehicleId) {
        setCommonAlert({ open: true, msg: labelText(t, "select_vehicle", "Select Vehicle") });
        return;
      }
      dispatch(assignFleetPrivateClientDriver({
        bookingId: booking.id,
        driverUid: userCombo.uid,
        vehicleId: selectedVehicleId
      }))
        .then((updatedBooking) => {
          setData(data.map((item) => item.id === updatedBooking.id ? updatedBooking : item));
          setUserCombo(null);
          setSelectedVehicleId("");
          handleClose();
          setCommonAlert({ open: true, msg: labelText(t, "driver_assigned_success", "Driver and vehicle assigned successfully.") });
        })
        .catch((error) => {
          setCommonAlert({ open: true, msg: error && error.message ? error.message : labelText(t, "driver_assign_failed", "Unable to assign driver and vehicle.") });
        });
      return;
    }
    if (isFleetAwaitingAssignment(booking)) {
      dispatch(assignFleetBidDriver({ bookingId: booking.id, driverUid: userCombo.uid }))
        .then((updatedBooking) => {
          if (userCombo.pushToken) {
            RequestPushMsg(userCombo.pushToken, {
              title: t("notification_title"),
              msg: t("new_booking_notification"),
              screen: "DriverTrips"
            });
          }
          if (booking.customer_token) {
            RequestPushMsg(booking.customer_token, {
              title: t("notification_title"),
              msg: labelText(t, "driver_assigned", "Driver assigned"),
              screen: "BookedCab",
              params: { bookingId: booking.id }
            });
          }
          setData(data.map((item) => item.id === updatedBooking.id ? updatedBooking : item));
          setUserCombo(null);
          handleClose();
          setCommonAlert({ open: true, msg: labelText(t, "driver_assigned_success", "Driver assigned successfully and notified.") });
        })
        .catch((error) => {
          setCommonAlert({ open: true, msg: error && error.message ? error.message : labelText(t, "driver_assign_failed", "Unable to assign driver.") });
        });
      return;
    }
    if (booking["requestedDrivers"]) {
      booking["requestedDrivers"][userCombo.uid] = true;
    } else {
      booking["requestedDrivers"] = {};
      booking["requestedDrivers"][userCombo.uid] = true;
    }
    dispatch(updateBooking(booking));
    RequestPushMsg(userCombo.pushToken, {
      title: t("notification_title"),
      msg: t("new_booking_notification"),
      screen: "DriverTrips"
    });
    setUserCombo(null);
    handleClose();
    alert("Driver assigned successfully and notified.");
  };

  const onConfirmClose = (value) => {
    if (!canEditBooking) {
      setOpenConfirm(false);
      setCommonAlert({ open: true, msg: label("action_permission_denied", "You do not have permission to edit bookings.") });
      return;
    }
    if (value) {
      dispatch(
        cancelBooking({
          reason: value,
          booking: selectedBooking,
          cancelledBy: role,
        })
      );
    }
    setOpenConfirm(false);
  };

  const handleChange = (e) => {
    if (e.target.name === "selectedProviderIndex") {
      setSelectedProviderIndex(parseInt(e.target.value));
      setSelectedProvider(providers[parseInt(e.target.value)]);
    }
    if (e.target.name === "selectedBidder") {
      setSelectedBidder(e.target.value);
    }
  };

  const hasBidOffers = (rowData) => {
    return getActiveDriverOffers(rowData).length > 0 || getActiveFleetOffers(rowData).length > 0;
  };

  const isFleetAwaitingAssignment = (rowData) => (
    rowData &&
    role === "fleetadmin" &&
    rowData.status === "AWAITING_DRIVER_ASSIGNMENT" &&
    rowData.acceptedOfferType === "fleet" &&
    (rowData.acceptedFleetAdmin === auth.profile.uid || rowData.fleetadmin === auth.profile.uid)
  );

  const handleClose = () => {
    setOpen(false);
    setUserCombo(null);
    setSelectedVehicleId("");
  };

  const handlePaymentModalClose = (e) => {
    setTimeout(() => {
      setPaymentModalStatus(false);
    }, 1500);
  };

  const handleWalletModalClose = (e) => {
    setTimeout(() => {
      setWalletModalStatus(false);
    }, 1500);
  };

  useEffect(() => {
    if (providers) {
      setSelectedProvider(providers[0]);
    }
  }, [providers]);

  const processPayment = async (rowData) => {
    const curBooking = rowData;
    const finalPaymentAmount = getFinalPaymentAmount(curBooking);
    if (finalPaymentAmount.pending) {
      setCommonAlert({ open: true, msg: labelText(t, "waiting_for_bid_acceptance", "Waiting for bid acceptance") });
      return;
    }
    const paymentCost = finalPaymentAmount.amount;
    if (rowData.status === "PAYMENT_PENDING" && (curBooking.payment_mode === "wallet" || curBooking.payment_mode === "cash")) {
      if (curBooking.payment_mode === "wallet" && parseFloat(paymentCost) > parseFloat(auth.profile.walletBalance)) {
        setCommonAlert({ open: true, msg: t("wallet_balance_low") });
        return;
      }
      try {
        await dispatch(settleBookingPayment(rowData.id));
        setCommonAlert({ open: true, msg: t("success_payment") });
      } catch (error) {
        setCommonAlert({ open: true, msg: error && error.message ? error.message : labelText(t, "payment_failed", "Payment activation failed") });
      }
      return;
    }
    if (curBooking.payment_mode === "card") {
      const paymentPacket = {
        payment_mode: "card",
        customer_paid: parseFloat(paymentCost).toFixed(settings.decimal),
        cardPaymentAmount: parseFloat(paymentCost).toFixed(settings.decimal),
        discount: 0,
        usedWalletMoney: 0,
        cashPaymentAmount: 0,
        promo_applied: false,
        promo_details: null,
        payableAmount: parseFloat(paymentCost).toFixed(settings.decimal),
      };
      curBooking.paymentPacket = paymentPacket;
      dispatch(updateBooking(curBooking));
      setSelectedBooking(curBooking);
      setPaymentModalStatus(true);
    }
    if (curBooking.payment_mode === "wallet") {
      let curBooking = { ...rowData };
      if (rowData.status === "PAYMENT_PENDING") {
        curBooking.status = "NEW";
        curBooking.prepaid = true;
      } else if (rowData.status === "PENDING") {
        curBooking.status = "PAID";
      } else if (rowData.status === "NEW") {
        curBooking.prepaid = true;
        curBooking.status = "ACCEPTED";
      }

      curBooking.customer_paid =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(paymentCost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(paymentCost).toFixed(settings.decimal);
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount =
        curBooking.status === "ACCEPTED"
          ? parseFloat(paymentCost).toFixed(settings.decimal)
          : parseFloat(paymentCost).toFixed(settings.decimal);

      if (curBooking.status === "ACCEPTED") {
        curBooking.driver = curBooking.selectedBid.driver;
        curBooking.driver_image = curBooking.selectedBid.driver_image;
        curBooking.driver_name = curBooking.selectedBid.driver_name;
        curBooking.driver_contact = curBooking.selectedBid.driver_contact;
        curBooking.driver_token = curBooking.selectedBid.driver_token;
        curBooking.vehicle_number = curBooking.selectedBid.vehicle_number;
        curBooking.vehicleModel = curBooking.selectedBid.vehicleModel;
        curBooking.vehicleMake = curBooking.selectedBid.vehicleMake;
        curBooking.driverRating = curBooking.selectedBid.driverRating;
        curBooking.trip_cost = paymentCost;
        curBooking.convenience_fees = curBooking.selectedBid.convenience_fees;
        curBooking.driver_share = curBooking.selectedBid.driver_share;
        curBooking.driverAcceptanceFeeTotal = curBooking.selectedBid.driverAcceptanceFeeTotal ? curBooking.selectedBid.driverAcceptanceFeeTotal : null;
        curBooking.driverAcceptanceFeeBreakdown = curBooking.selectedBid.driverAcceptanceFeeBreakdown ? curBooking.selectedBid.driverAcceptanceFeeBreakdown : null;
        curBooking.fleetadmin = curBooking.selectedBid.fleetadmin ? curBooking.selectedBid.fleetadmin : null;
        curBooking.fleetCommission= curBooking.selectedBid.fleetCommission ? curBooking.selectedBid.fleetCommission : (curBooking.selectedBid.fleetadmin ? ((parseFloat( curBooking.selectedBid.trip_cost) - parseFloat(curBooking.selectedBid.convenience_fees)) * (parseFloat(curBooking.fleet_admin_comission) / 100)).toFixed(2) : null);
        curBooking.driverOffers = {};
        curBooking.requestedDrivers = {};
        curBooking.driverEstimates = {};
        curBooking.selectedBid = {};
      }
      dispatch(updateBooking(curBooking));

      setBidModalStatus(false);
    }
    if (curBooking.payment_mode === "cash") {
      let curBooking = { ...rowData };
      if (rowData.status === "PAYMENT_PENDING") {
        curBooking.status = "NEW";
      } else if (rowData.status === "PENDING") {
        curBooking.status = "PAID";
      } else if (rowData.status === "NEW") {
        curBooking.status = "ACCEPTED";
      }

      curBooking.customer_paid =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(paymentCost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney = 0;
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(paymentCost).toFixed(settings.decimal);
      curBooking.payableAmount =
        curBooking.status === "ACCEPTED"
          ? parseFloat(paymentCost).toFixed(settings.decimal)
          : parseFloat(paymentCost).toFixed(settings.decimal);

      if (curBooking.status === "ACCEPTED") {
        curBooking.driver = curBooking.selectedBid.driver;
        curBooking.driver_image = curBooking.selectedBid.driver_image;
        curBooking.driver_name = curBooking.selectedBid.driver_name;
        curBooking.driver_contact = curBooking.selectedBid.driver_contact;
        curBooking.driver_token = curBooking.selectedBid.driver_token;
        curBooking.vehicle_number = curBooking.selectedBid.vehicle_number;
        curBooking.vehicleModel = curBooking.selectedBid.vehicleModel;
        curBooking.vehicleMake = curBooking.selectedBid.vehicleMake;
        curBooking.driverRating = curBooking.selectedBid.driverRating;
        curBooking.trip_cost = paymentCost;
        curBooking.convenience_fees = curBooking.selectedBid.convenience_fees;
        curBooking.driver_share = curBooking.selectedBid.driver_share;
        curBooking.driverAcceptanceFeeTotal = curBooking.selectedBid.driverAcceptanceFeeTotal ? curBooking.selectedBid.driverAcceptanceFeeTotal : null;
        curBooking.driverAcceptanceFeeBreakdown = curBooking.selectedBid.driverAcceptanceFeeBreakdown ? curBooking.selectedBid.driverAcceptanceFeeBreakdown : null;
        curBooking.fleetadmin = curBooking.selectedBid.fleetadmin ? curBooking.selectedBid.fleetadmin : null;
        curBooking.fleetCommission= curBooking.selectedBid.fleetCommission ? curBooking.selectedBid.fleetCommission : (curBooking.selectedBid.fleetadmin ? ((parseFloat( curBooking.selectedBid.trip_cost) - parseFloat(curBooking.selectedBid.convenience_fees)) * parseFloat(curBooking.fleet_admin_comission) / 100).toFixed(2):null);
        curBooking.driverOffers = {};
        curBooking.requestedDrivers = {};
        curBooking.driverEstimates = {};
        curBooking.selectedBid = {};
      }
      dispatch(updateBooking(curBooking));
      setBidModalStatus(false);
    }
  };

  const resendBookingReceipt = (rowData) => {
    if (!canEditBooking) {
      setCommonAlert({ open: true, msg: label("action_permission_denied", "You do not have permission to edit bookings.") });
      return;
    }
    dispatch(sendBookingReceipt(rowData.id)).then(() => {
      setCommonAlert({ open: true, msg: label("booking_receipt_sent", "Booking receipt sent") });
    }).catch((error) => {
      setCommonAlert({ open: true, msg: error && error.message ? error.message : label("booking_receipt_failed", "Unable to send booking receipt") });
    });
  };

  const placeOrderAgain = (rowData) => {
    navigate("/addbookings", {
      state: {
        reorderDraft: createReorderDraft(rowData),
        pageNo: currentPage
      }
    });
  };

  const handleWalletPayment = (e) => {
    e.preventDefault();
    let curBooking = { ...selectedBooking };
    const finalPaymentAmount = getFinalPaymentAmount(curBooking);
    if (finalPaymentAmount.pending) {
      setCommonAlert({ open: true, msg: labelText(t, "waiting_for_bid_acceptance", "Waiting for bid acceptance") });
      return;
    }
    let paymentCost = finalPaymentAmount.amount;
    if (parseFloat(paymentCost) > parseFloat(auth.profile.walletBalance)) {
      setCommonAlert({ open: true, msg: t("wallet_balance_low") });
    } else {
      curBooking.prepaid =
        curBooking.status === "NEW" || curBooking.status === "PAYMENT_PENDING"
          ? true
          : false;
      curBooking.status = curBooking.status === "NEW" ? "ACCEPTED" : "PAID";
      curBooking.customer_paid = parseFloat(paymentCost).toFixed(
        settings.decimal
      );
      curBooking.discount = 0;
      curBooking.usedWalletMoney = parseFloat(paymentCost).toFixed(
        settings.decimal
      );
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount = 0;
      curBooking.promo_applied = false;
      curBooking.promo_details = null;
      curBooking.paymentPacket = null;
      curBooking.driver = curBooking.selectedBid
        ? curBooking.selectedBid.driver
        : curBooking.driver;
      curBooking.driver_image = curBooking.selectedBid
        ? curBooking.selectedBid.driver_image
        : curBooking.driver_image;
      curBooking.driver_name = curBooking.selectedBid
        ? curBooking.selectedBid.driver_name
        : curBooking.driver_name;
      curBooking.driver_contact = curBooking.selectedBid
        ? curBooking.selectedBid.driver_contact
        : curBooking.driver_contact;
      curBooking.driver_token = curBooking.selectedBid
        ? curBooking.selectedBid.driver_token
        : curBooking.driver_token;
      curBooking.vehicle_number = curBooking.selectedBid
        ? curBooking.selectedBid.vehicle_number
        : curBooking.vehicle_number;
      curBooking.vehicleModel = curBooking.selectedBid
        ? curBooking.selectedBid.vehicleModel
        : curBooking.vehicleModel;
      curBooking.vehicleMake = curBooking.selectedBid
        ? curBooking.selectedBid.vehicleModel
        : curBooking.vehicleModel;
      curBooking.driverRating = curBooking.selectedBid
        ? curBooking.selectedBid.driverRating
        : curBooking.driverRating;
      curBooking.trip_cost = curBooking.selectedBid
        ? paymentCost
        : curBooking.trip_cost;
      curBooking.convenience_fees = curBooking.selectedBid
        ? curBooking.selectedBid.convenience_fees
        : curBooking.convenience_fees;
      curBooking.driver_share = curBooking.selectedBid
        ? curBooking.selectedBid.driver_share
        : curBooking.driver_share;
      curBooking.driverAcceptanceFeeTotal = curBooking.selectedBid
        ? curBooking.selectedBid.driverAcceptanceFeeTotal
        : curBooking.driverAcceptanceFeeTotal;
      curBooking.driverAcceptanceFeeBreakdown = curBooking.selectedBid
        ? curBooking.selectedBid.driverAcceptanceFeeBreakdown
        : curBooking.driverAcceptanceFeeBreakdown;
      curBooking.driverOffers = null;
      curBooking.requestedDrivers = null;
      curBooking.driverEstimates = null;
      curBooking.selectedBid = null;

      dispatch(updateBooking(curBooking));

      setTimeout(() => {
        setWalletModalStatus(false);
      }, 1500);
    }
  };

  const handleBidModalClose = () => {
    setBidModalStatus(false);
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const getBookingRowActions = (rowData) => {
    const rowActions = [
      {
        id: "cancel",
        label:
          role === "fleetadmin" && !canCancelBookingForRole(rowData, role)
            ? label("cannot_cancel_after_accepted", "Cannot cancel after accepted")
            : t("cancel_booking"),
        icon: <CancelIcon fontSize="small" />,
        disabled: !canEditBooking || !canCancelBookingForRole(rowData, role),
        danger: true,
        onClick: () => {
          if (!canEditBooking || !canCancelBookingForRole(rowData, role)) {
            return;
          }
          if (
            settings.AllowCriticalEditsAdmin &&
            (role === "customer" || role === "admin" || role === "fleetadmin")
          ) {
            if (canCancelBookingForRole(rowData, role)) {
              setSelectedBooking(rowData);
              setOpenConfirm(true);
            } else {
              setTimeout(() => {
                dispatch(
                  cancelBooking({
                    reason: t("cancelled_incomplete_booking"),
                    booking: rowData,
                    cancelledBy: role,
                  })
                );
              }, 1500);
            }
          } else {
            alert(t("demo_mode"));
          }
        },
      },
    ];

    if (
      canEditBooking &&
      rowData.status === "NEW" &&
      role === "customer" &&
      !rowData.acceptedOfferType &&
      hasBidOffers(rowData)
    ) {
      rowActions.push({
        id: "select-bid",
        label: t("selectBid"),
        icon: <PlaylistAddCheckIcon fontSize="small" />,
        primary: true,
        onClick: () => {
          setRowIndex(rowData.tableData.id);
          setSelectedBooking(rowData);
          setSelectedBidder();
          setBidModalStatus(true);
        },
      });
    }

    if (
      canEditBooking &&
      ((rowData.status === "NEW" &&
        (role === "admin" || role === "fleetadmin") &&
        settings.autoDispatch === false &&
        showEst) ||
        isFleetAwaitingAssignment(rowData) ||
        (rowData.status === "NEW" && isFleetPrivateOwnerBooking(rowData)))
    ) {
      rowActions.push({
        id: "assign-driver",
        label: t("assign_driver"),
        icon: <PersonAddIcon fontSize="small" />,
        primary: true,
        onClick: () => {
          setOpen(true);
          setRowIndex(rowData.tableData.id);
          setSelectedBooking(rowData);
          setUserCombo(null);
          setSelectedVehicleId("");
        },
      });
    }

    if (
      canEditBooking &&
      (rowData.status === "PAYMENT_PENDING" || rowData.status === "PENDING") &&
      role === "customer"
    ) {
      rowActions.push({
        id: "pay-now",
        label: t("paynow_button"),
        icon: <PaymentIcon fontSize="small" />,
        primary: true,
        onClick: () => processPayment(rowData),
      });
    }

    if (canEditBooking && rowData.status === "STARTED" && role === "admin") {
      rowActions.push({
        id: "force-end",
        label: t("force_end"),
        icon: <PlaylistAddCheckIcon fontSize="small" />,
        primary: true,
        onClick: () => dispatch(forceEndBooking(rowData)),
      });
    }

    if (canEditBooking && role === "admin") {
      rowActions.push({
        id: "send-booking-receipt",
        label: label("send_booking_receipt", "Send Booking Receipt"),
        icon: <EmailIcon fontSize="small" />,
        onClick: () => resendBookingReceipt(rowData),
      });
    }

    if (canCreateBooking && canPlaceOrderAgainForRole(rowData, role)) {
      rowActions.push({
        id: "place-order-again",
        label: label("place_order_again", "Place Order Again"),
        icon: <ReplayIcon fontSize="small" />,
        primary: true,
        onClick: () => placeOrderAgain(rowData),
      });
    }

    rowActions.push({
      id: "details",
      label: t("booking_details"),
      icon: <InfoOutlinedIcon fontSize="small" />,
      onClick: () =>
        navigate(`/bookings/bookingdetails/${rowData.id}`, {
          state: { pageNo: currentPage },
        }),
    });

    return rowActions;
  };

  const tableColumns = [
    ...columns,
    {
      title: label("actions", "Actions"),
      field: "bookingRowActions",
      sorting: false,
      filtering: false,
      searchable: false,
      export: false,
      cellStyle: {
        width: 210,
        minWidth: 210,
        maxWidth: 210,
        padding: "6px 10px",
        whiteSpace: "nowrap",
      },
      headerStyle: {
        width: 210,
        minWidth: 210,
        maxWidth: 210,
        textAlign: "right",
      },
      render: (rowData) => (
        <BookingRowActions
          actions={getBookingRowActions(rowData)}
          moreLabel={label("more_actions", "More actions")}
        />
      ),
    },
  ];

  const [selectedRow, setSelectedRow] = useState(null);
  const inputStyle = { fontFamily: FONT_FAMILY, };
  const bookingSummary = [
    {
      label: label("total_bookings", "Total Bookings"),
      value: data.length,
      tone: "info",
      helper: label("booking_title", "Bookings")
    },
    {
      label: label("active_bookings", "Active Bookings"),
      value: data.filter((booking) => ["ACCEPTED", "STARTED", "REACHED"].indexOf((booking.status || "").toString().toUpperCase()) !== -1).length,
      tone: "purple",
      helper: label("in_progress", "In progress")
    },
    {
      label: label("pending_bookings", "Pending Bookings"),
      value: data.filter((booking) => ["NEW", "PENDING", "PAYMENT_PENDING", "AWAITING_DRIVER_ASSIGNMENT"].indexOf((booking.status || "").toString().toUpperCase()) !== -1).length,
      tone: "warning",
      helper: label("awaiting_action", "Awaiting action")
    },
    {
      label: label("completed_bookings", "Completed Bookings"),
      value: data.filter((booking) => ["COMPLETE", "COMPLETED"].indexOf((booking.status || "").toString().toUpperCase()) !== -1).length,
      tone: "success",
      helper: label("closed_trips", "Closed trips")
    }
  ];

  if (bookinglistdata.error && bookinglistdata.error.flag) {
    return <BookingDataLoadError message={bookinglistdata.error.msg} />;
  }

  return bookinglistdata.loading ? (
    <CircularLoading />
  ) : (
    <Box className="garahex-bookings-page">
      <PageHeader
        eyebrow={label("booking_operations", "Booking Operations")}
        title={t("booking_title")}
        description={label("booking_history_description", "Monitor every shipment, assignment, bid, payment state, and return-ready booking from one premium control surface.")}
        actions={
          canCreateBooking ? (
            <ActionButton startIcon={<PersonAddIcon />} onClick={() => navigate("/addbookings")}>
              {label("addbookinglable", "New Booking")}
            </ActionButton>
          ) : null
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {bookingSummary.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <SurfaceCard sx={{ minHeight: 128 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                <Box>
                  <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: adminTokens.colors.text, fontSize: "2rem", fontWeight: 950, lineHeight: 1.05, mt: 0.8 }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ color: adminTokens.colors.muted, fontSize: 12, mt: 0.75 }}>
                    {item.helper}
                  </Typography>
                </Box>
                <StatusBadge tone={item.tone}>{item.tone}</StatusBadge>
              </Box>
            </SurfaceCard>
          </Grid>
        ))}
      </Grid>

      <SurfaceCard>
        <SectionHeader
          title={label("booking_control_table", "Booking Control Table")}
          description={label("booking_control_table_description", "Existing search, export, pagination, row selection, and booking actions are preserved.")}
          actions={<StatusBadge tone="info">{`${data.length} ${label("records", "records")}`}</StatusBadge>}
        />
        <AdminDataTable
          title={t("booking_title")}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
          }}
          onChangePage={(page) => handlePaginationChange(page)}
          columns={tableColumns}
          data={data}
          emptyTitle={label("no_bookings_found", "No bookings found")}
          emptyDescription={label("no_bookings_found_description", "Booking data is loaded through the existing scoped booking path.")}
          onRowClick={(evt, selectedRow) =>
            setSelectedRow(selectedRow.tableData.id)
          }
          options={{
            initialPage:state?.pageNo,
            toolbarbuttonalignment: "right",
            exportCsv: (columns, data) => {
              let hArray = [];
              const headerRow = columns.map((col) => {
                if (typeof col.title === "object") {
                  return col.title.props.text;
                }
                hArray.push(col.field);
                return col.title;
              });
              const dataRows = data.map(({ tableData, ...row }) => {
                row.bookingDate =
                  new Date(row.bookingDate).toLocaleDateString() +
                  " " +
                  new Date(row.bookingDate).toLocaleTimeString();
                row.tripdate =
                  new Date(row.tripdate).toLocaleDateString() +
                  " " +
                  new Date(row.tripdate).toLocaleTimeString();
                row.pickupAddress = row.pickupAddress.replace(/,/g, ";");
                row.dropAddress = row.dropAddress.replace(/,/g, ";");
                let dArr = [];
                for (let i = 0; i < hArray.length; i++) {
                  dArr.push(row[hArray[i]]);
                }
                return Object.values(dArr);
              });
              const { exportDelimiter } = ",";
              const delimiter = exportDelimiter ? exportDelimiter : ",";
              const csvContent = [headerRow, ...dataRows]
                .map((e) => e.join(delimiter))
                .join("\n");
              const csvFileName = "download.csv";
              downloadCsv(csvContent, csvFileName);
            },
            exportButton: {
              csv: settings.AllowCriticalEditsAdmin,
              pdf: false,
            },
            maxColumnSort: "all_columns",
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" : colors.WHITE
            }),
            maxBodyHeight: "calc(100vh - 199.27px)",
          }}
          localization={localization(t)}
        />
      </SurfaceCard>
      <BidModal
        ref={rootRef.current}
        role={role}
        selectedBooking={selectedBooking}
        bidModalStatus={bidModalStatus}
        handleBidModalClose={handleBidModalClose}
        classes={classes}
        settings={settings}
        acceptBid={() => {
          if (!canEditBooking) {
            setCommonAlert({ open: true, msg: label("action_permission_denied", "You do not have permission to edit bookings.") });
            return;
          }
          if (!selectedBidder) {
            setCommonAlert({ open: true, msg: labelText(t, "selectBid", "Select Bid") });
            return;
          }
          const bookingObj = acceptBid(selectedBooking, selectedBidder);
          processPayment(bookingObj);
          handleBidModalClose();
        }}
        acceptFleetBid={(fleetAdminUid) => {
          if (!canEditBooking) {
            setCommonAlert({ open: true, msg: label("action_permission_denied", "You do not have permission to edit bookings.") });
            return;
          }
          dispatch(acceptFleetOffer({ bookingId: selectedBooking.id, fleetAdminUid }))
            .then(() => {
              setCommonAlert({ open: true, msg: labelText(t, "fleet_offer_accepted", "Transport company offer accepted.") });
              handleBidModalClose();
            })
            .catch((error) => {
              setCommonAlert({ open: true, msg: error && error.message ? error.message : labelText(t, "fleet_offer_accept_failed", "Unable to accept fleet offer.") });
            });
        }}
        selectedBidder={selectedBidder}
        handleChange={handleChange}
      />
      {selectedBooking &&
      selectedBooking.payment_mode === "wallet" &&
      (selectedBooking.status === "PENDING" ||
        (selectedBooking &&
          selectedBooking.selectedBid &&
          selectedBooking.status === "NEW")) &&
      role === "customer" ? (
        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          open={walletModalStatus}
          onClose={handleWalletModalClose}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <Grid container spacing={2} className={classes.paper}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              style={{ marginBottom: "20px" }}
            >
              <Typography style={{ fontWeight: "bolder", marginBottom: 10 ,fontFamily: FONT_FAMILY,}}>
                {t("payment")}
              </Typography>
              {selectedBooking ? (
                <Typography color={"primary"} style={{ fontSize: 30 ,fontFamily: FONT_FAMILY}}>
                  {getFinalPaymentAmount(selectedBooking).pending
                    ? labelText(t, "waiting_for_bid_acceptance", "Waiting for bid acceptance")
                    : formatCurrency(getFinalPaymentAmount(selectedBooking).amount, settings)}
                </Typography>
              ) : null}
              <Typography style={{fontFamily: FONT_FAMILY}}>
                {t("use_wallet_balance") +
                  " " +
                  formatCurrency(auth.profile.walletBalance, settings) +
                  ")"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Button
                onClick={handleWalletModalClose}
                variant="contained"
                style={{backgroundColor:colors.RED, fontFamily: FONT_FAMILY }}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 10 , backgroundColor:colors.GREEN, fontFamily: FONT_FAMILY }}
                onClick={handleWalletPayment}
              >
                {t("paynow_button")}
              </Button>
            </Grid>
          </Grid>
        </Modal>
      ) : null}
      {selectedBooking &&
      selectedBooking.paymentPacket &&
      selectedBooking.payment_mode === "card" &&
      ((selectedBooking.status === "PENDING" || selectedBooking.status === "PAYMENT_PENDING") ||
        (selectedBooking &&
          selectedBooking.selectedBid &&
          selectedBooking.status === "NEW")) &&
      role === "customer" ? (
        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          open={paymentModalStatus}
          onClose={handlePaymentModalClose}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <Grid container spacing={2} className={classes.paper}>
            {providers && selectedProvider && selectedBooking ? (
              <form action={selectedProvider.link} method="POST">
                <input
                  type="hidden"
                  name="order_id"
                  value={selectedBooking.id}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="amount"
                  value={getFinalPaymentAmount(selectedBooking).pending ? 0 : getFinalPaymentAmount(selectedBooking).amount}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="currency"
                  value={settings.code}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="product_name"
                  value={t("bookingPayment")}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="first_name"
                  value={auth.profile.firstName}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="last_name"
                  value={auth.profile.lastName}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="quantity"
                  value={1}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="cust_id"
                  value={selectedBooking.customer}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="mobile_no"
                  value={selectedBooking.customer_contact}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="email"
                  value={selectedBooking.customer_email}
                  style={inputStyle}
                />
                <input
                  type="hidden"
                  name="payment_method"
                  value={selectedProvider.method || selectedProvider.name}
                  style={inputStyle}
                />
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  style={{ marginBottom: "20px" }}
                >
               <FormControl fullWidth style={{ fontFamily: FONT_FAMILY }}>
                  <FormLabel component="legend" style={{ fontFamily: FONT_FAMILY }}> {t("payment")} </FormLabel>
                  <Select
                    id="selectedProviderIndex"
                    name="selectedProviderIndex"
                    value={selectedProviderIndex}
                    label={t("payment")}
                    onChange={handleChange}
                    style={{ textAlign: isRTL === "rtl" ? "right" : "left", fontFamily: FONT_FAMILY }}
                    inputProps={{ "aria-label": "Without label", style: { fontFamily: FONT_FAMILY } }}
                  >
                    {providers.map((provider, index) => (
                      <MenuItem key={provider.name} value={index} style={{ fontFamily: FONT_FAMILY }}>
                        <img
                          style={{ height: 24, margin: 7 }}
                          src={icons[provider.name]}
                          alt={provider.name}
                        />{" "}
                        {paymentProviderLabel(provider)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Button
                    onClick={handlePaymentModalClose}
                    variant="contained"
                    style={{  backgroundColor:colors.RED,fontFamily: FONT_FAMILY }}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginLeft: 10 , backgroundColor:colors.GREEN, fontFamily: FONT_FAMILY }}
                    onClick={handlePaymentModalClose}
                  >
                    {t("paynow_button")}
                  </Button>
                </Grid>
              </form>
            ) : null}
          </Grid>
        </Modal>
      ) : null}
      <ConfirmationDialogRaw
        open={openConfirm}
        onClose={onConfirmClose}
        value={""}
      />
      {users && data && rowIndex >= 0 ? (
        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          onClose={handleClose}
          open={open}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <div className={classes.paper}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  component="h1"
                  variant="h5"
                  className={classes.title}
                  style={{ textAlign: isRTL === "rtl" ? "right" : "left", fontFamily: FONT_FAMILY}}
                >
                  {t("select_driver")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <UsersCombo
                  className={classes.items}
                  placeholder={t("select_user")}
                  users={users.filter(
                    (usr) => normalizeVehicleType(usr.carType) === normalizeVehicleType((selectedBooking || data[rowIndex]).carType)
                  )}
                  value={userCombo}
                  onChange={(event, newValue) => {
                    setUserCombo(newValue);
                    setSelectedVehicleId("");
                  }}
                />
              </Grid>
              {isFleetPrivateOwnerBooking(selectedBooking || data[rowIndex]) ? (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel>{labelText(t, "select_vehicle", "Select Vehicle")}</FormLabel>
                    <Select
                      value={selectedVehicleId}
                      onChange={(event) => setSelectedVehicleId(event.target.value)}
                    >
                      <MenuItem value="">
                        {labelText(t, "select_vehicle", "Select Vehicle")}
                      </MenuItem>
                      {getPrivateAssignmentVehicles(selectedBooking || data[rowIndex], userCombo).map((car) => (
                        <MenuItem key={car.id} value={car.id}>
                          {getVehicleLabel(car)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : null}
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                style={{
                  direction: isRTL === "rtl" ? "rtl" : "ltr",
                  marginLeft: isRTL === "rtl" ? "65%" : 0,
                }}
              >
                <Button
                  onClick={handleClose}
                  variant="contained"
                  style={{  backgroundColor:colors.RED, fontFamily: FONT_FAMILY }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  onClick={assignDriver}
                  variant="contained"
                  color="primary"
                  style={{
                    ...(isRTL === "rtl" ? { marginRight: 10 } : { marginLeft: 10 }),
                    backgroundColor:colors.RED,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {t("assign")}
                </Button>
              </Grid>
            </Grid>
          </div>
        </Modal>
      ) : null}
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </Box>
  );
};

export default BookingHistory;
